import React, { useEffect, useState, useMemo } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Save, Trash2, Star, ExternalLink, Search, Users, Sparkles, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATEGORIES = [
  { v: "opponent_scout", l: "Opponent Scout", tone: "text-sky-400 border-sky-500/40" },
  { v: "game_film", l: "Game Film", tone: "text-emerald-400 border-emerald-500/40" },
  { v: "cutups", l: "Cut-ups", tone: "text-amber border-amber/50" },
  { v: "practice_film", l: "Practice Film", tone: "text-slate-300 border-slate-500/40" },
];

function catMeta(v) {
  return CATEGORIES.find(c => c.v === v) || CATEGORIES[3];
}

function ClipCard({ clip, onDelete, onToggleFav, onEdit }) {
  const meta = catMeta(clip.category);
  return (
    <div className="bg-surface border border-surface-3 p-5 relative group hover:border-amber/40 transition-colors" data-testid={`clip-${clip.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] font-condensed uppercase tracking-widest border px-1.5 py-0.5 ${meta.tone}`}>
          {meta.l}
        </span>
        {clip.game_date && (
          <span className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">{clip.game_date}</span>
        )}
      </div>
      <div className="font-display text-xl uppercase tracking-wide leading-tight text-white mt-2">{clip.title}</div>
      {clip.notes && <div className="text-sm text-slate-300 mt-2 line-clamp-3">{clip.notes}</div>}
      {(clip.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {clip.tags.map(t => (
            <span key={t} className="text-[9px] font-condensed uppercase tracking-widest text-slate-400 border border-surface-3 px-1.5 py-0.5">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-surface-3 flex items-center justify-between">
        {clip.hudl_url ? (
          <a href={clip.hudl_url} target="_blank" rel="noreferrer"
             data-testid={`clip-link-${clip.id}`}
             className="text-[10px] font-condensed uppercase tracking-widest text-amber hover:text-amber-400 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Open on Hudl
          </a>
        ) : <span className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">No link</span>}
        <div className="flex items-center gap-3">
          <button onClick={() => onToggleFav(clip)} data-testid={`clip-fav-${clip.id}`}
            className={`transition-colors ${clip.favorite ? "text-amber" : "text-slate-500 hover:text-amber"}`}>
            <Star className="w-4 h-4" fill={clip.favorite ? "#F59E0B" : "none"} />
          </button>
          <button onClick={() => onEdit(clip)} data-testid={`clip-edit-${clip.id}`}
            className="text-[10px] font-condensed uppercase tracking-widest text-slate-400 hover:text-amber">Edit</button>
          <button onClick={() => onDelete(clip)} data-testid={`clip-del-${clip.id}`}
            className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ClipEditor({ clip, onSaved, onCancel }) {
  const [c, setC] = useState(clip);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setC(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!c.title?.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const payload = {
        title: c.title, opponent: c.opponent || "",
        game_date: c.game_date || null, hudl_url: c.hudl_url || "",
        category: c.category || "game_film",
        notes: c.notes || "",
        tags: typeof c.tags === "string"
          ? c.tags.split(",").map(s => s.trim()).filter(Boolean)
          : (c.tags || []),
        favorite: !!c.favorite,
        linked_play_ids: c.linked_play_ids || [],
      };
      const { data } = c.id ? await api.put(`/film/${c.id}`, payload) : await api.post("/film", payload);
      onSaved(data);
      toast.success("Clip saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-surface border border-amber/40 p-6" data-testid="film-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="font-condensed uppercase tracking-[0.25em] text-sm">{c.id ? "Edit Clip" : "New Film Clip"}</div>
        <button onClick={onCancel} className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 hover:text-white">Cancel</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Title</div>
          <Input value={c.title || ""} onChange={(e)=>set("title", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="clip-title" placeholder="e.g., Central Prep — Offense Cutups" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Opponent</div>
          <Input value={c.opponent || ""} onChange={(e)=>set("opponent", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="clip-opponent" placeholder="Leave blank for self-scout/cutups" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Game Date</div>
          <Input type="date" value={c.game_date || ""} onChange={(e)=>set("game_date", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" />
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Category</div>
          <Select value={c.category || "game_film"} onValueChange={(v)=>set("category", v)}>
            <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none" data-testid="clip-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              {CATEGORIES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Hudl URL</div>
          <Input value={c.hudl_url || ""} onChange={(e)=>set("hudl_url", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" data-testid="clip-hudl" placeholder="https://www.hudl.com/video/..." />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Coaching Notes</div>
          <Textarea value={c.notes || ""} onChange={(e)=>set("notes", e.target.value)} rows={3}
            className="mt-1 bg-background border-surface-3 rounded-none text-sm" data-testid="clip-notes" />
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Tags (comma-separated)</div>
          <Input
            value={Array.isArray(c.tags) ? c.tags.join(", ") : (c.tags || "")}
            onChange={(e)=>set("tags", e.target.value)}
            className="mt-1 bg-background border-surface-3 rounded-none" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="clip-save"
          className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Clip"}
        </Button>
      </div>
    </div>
  );
}

export default function FilmHub() {
  const [clips, setClips] = useState([]);
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterOpp, setFilterOpp] = useState("all");
  const [editing, setEditing] = useState(null); // {clip} or null
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null); // opponent name currently generating
  const nav = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const [clipsRes, reportsRes] = await Promise.all([
        api.get("/film"),
        api.get("/scout-reports"),
      ]);
      setClips(clipsRes.data);
      setReports(reportsRes.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const generateScout = async (opponent) => {
    setGenerating(opponent);
    try {
      const { data } = await api.post("/scout-reports", { opponent });
      toast.success(`Scout report ready for ${opponent}`);
      nav(`/scout-report/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = detail?.message || (typeof detail === "string" ? detail : "Generation failed");
      toast.error(msg);
    } finally { setGenerating(null); }
  };

  const opponents = useMemo(() => {
    const set = new Set(clips.map(c => c.opponent || "Unspecified"));
    return Array.from(set).sort();
  }, [clips]);

  const filtered = useMemo(() => {
    return clips.filter(c => {
      if (filterCat !== "all" && c.category !== filterCat) return false;
      if (filterOpp !== "all") {
        const opp = c.opponent || "Unspecified";
        if (opp !== filterOpp) return false;
      }
      if (q) {
        const hay = `${c.title} ${c.opponent} ${c.notes} ${(c.tags||[]).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [clips, filterCat, filterOpp, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(c => {
      const opp = c.opponent || "Self-Scout & Cut-ups";
      (g[opp] = g[opp] || []).push(c);
    });
    return g;
  }, [filtered]);

  const onDelete = async (clip) => {
    if (!window.confirm("Delete this clip link?")) return;
    await api.delete(`/film/${clip.id}`);
    setClips(clips.filter(x => x.id !== clip.id));
    toast.success("Deleted");
  };

  const onToggleFav = async (clip) => {
    const { data } = await api.put(`/film/${clip.id}`, { ...clip, favorite: !clip.favorite });
    setClips(clips.map(x => x.id === clip.id ? data : x));
  };

  const onSaved = (saved) => {
    setClips(prev => {
      const others = prev.filter(x => x.id !== saved.id);
      return [saved, ...others];
    });
    setEditing(null);
  };

  return (
    <div data-testid="film-hub-page">
      <TopBar
        title="Film Hub"
        subtitle="Hudl links, organized by opponent. AI Coach references these in scout reports."
        actions={
          <Button onClick={() => setEditing({ id: null, title: "", opponent: "", category: "opponent_scout", hudl_url: "", notes: "", tags: [] })}
            data-testid="new-clip-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-[0.25em]">
            <Plus className="w-4 h-4 mr-1" /> New Film Link
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-3 bg-surface border border-surface-3 p-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search film…"
              className="bg-background border-surface-3 rounded-none pl-9" data-testid="film-search" />
          </div>
          <div className="flex items-center gap-1 border border-surface-3">
            <button onClick={()=>setFilterCat("all")}
              data-testid="film-filter-all"
              className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 ${filterCat === "all" ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c.v} onClick={()=>setFilterCat(c.v)}
                data-testid={`film-filter-${c.v}`}
                className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 ${filterCat === c.v ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"}`}>{c.l}</button>
            ))}
          </div>
          <Select value={filterOpp} onValueChange={setFilterOpp}>
            <SelectTrigger className="w-[220px] bg-background border-surface-3 rounded-none h-10" data-testid="film-filter-opp">
              <SelectValue placeholder="All opponents" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              <SelectItem value="all">All opponents</SelectItem>
              {opponents.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {editing && <ClipEditor clip={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />}

        {/* Recent Scout Reports */}
        {reports.length > 0 && (
          <div className="bg-surface border border-surface-3" data-testid="scout-reports-strip">
            <div className="px-6 py-3 border-b border-surface-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-sm">Recent Scout Reports</div>
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 ml-3">{reports.length}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-x divide-y divide-surface-3 border-b border-surface-3">
              {reports.slice(0, 6).map(r => (
                <button
                  key={r.id}
                  onClick={() => nav(`/scout-report/${r.id}`)}
                  data-testid={`report-item-${r.id}`}
                  className="text-left px-5 py-4 hover:bg-surface-2 transition-colors"
                >
                  <div className="text-[10px] font-condensed uppercase tracking-widest text-amber">
                    vs. {r.opponent}
                  </div>
                  <div className="font-semibold text-sm mt-1">Scout Report</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(r.created_at).toLocaleDateString()} · {r.clip_count} clip{r.clip_count !== 1 ? "s" : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="text-sm text-slate-400">Loading film library…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-sm text-slate-500 italic border border-dashed border-surface-3 p-12 text-center">
            No film clips match. Try clearing filters or add a new Hudl link.
          </div>
        )}

        {Object.keys(grouped).sort().map(opp => (
          <div key={opp} data-testid={`opp-group-${opp}`}>
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-4 h-4 text-amber" />
              <div className="font-display text-2xl uppercase tracking-wide text-white">{opp}</div>
              <div className="hairline flex-1" />
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">{grouped[opp].length} clip{grouped[opp].length !== 1 ? "s" : ""}</div>
              {opp !== "Self-Scout & Cut-ups" && (
                <button
                  onClick={() => generateScout(opp)}
                  disabled={generating === opp}
                  data-testid={`scout-btn-${opp.replace(/\s+/g,'-')}`}
                  className="bg-amber hover:bg-amber-600 text-[#0B0D12] px-3 py-1.5 font-condensed uppercase tracking-widest text-[10px] flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  <Sparkles className="w-3 h-3" />
                  {generating === opp ? "Generating…" : "AI Scout Report"}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {grouped[opp].map(c => (
                <ClipCard key={c.id} clip={c} onDelete={onDelete} onToggleFav={onToggleFav} onEdit={setEditing} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
